/*
  Custom Colour picker.

  Drives the same <mix-match-board> preview as the Mix & Match product, but each side takes
  a HEX colour rather than a design. The product's own Sides option decides whether the back
  colour is asked for, so a Single Sided variant never submits a second colour.

  Line item properties keep the names the Infinite Options fields already submit, so orders
  read the same as they always have.
*/
(function () {
  'use strict';

  const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;
  const SIZE_RE = /(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i;

  // Canopy binds its option handlers on its own bundled jQuery, which is not necessarily
  // window.jQuery, so always talk to the theme through that instance.
  function themeJQ() {
    return (window.theme && theme.jQuery) || window.jQuery || null;
  }

  function normaliseHex(value) {
    const raw = String(value || '').trim();
    if (!HEX_RE.test(raw)) return null;
    const body = raw.replace('#', '');
    const full = body.length === 3 ? body.split('').map((c) => c + c).join('') : body;
    return '#' + full.toUpperCase();
  }

  class CustomColorPicker extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      this.container = this.closest('.product-container');
      this.board = this.container && this.container.querySelector('mix-match-board');
      this.ambient = this.container && this.container.querySelector('mix-match-ambient');
      this.steps = Array.from(this.querySelectorAll('[data-mm-step]'));
      this.sides = this.steps.filter((s) => s.dataset.mmSide);
      this.colors = { front: null, back: null };
      this.doubleSided = true;

      this.steps.forEach((step) => this.initStep(step));
      this.sides.forEach((side) => this.initSide(side));
      this.adoptVariantPicker();
      this.retireInfiniteOptions();

      if (this.form && this.form.customColorPicker !== this) {
        // Capture phase so this runs before the theme's own ajax submit handler.
        this.boundSubmit = (event) => this.onSubmit(event);
        this.form.addEventListener('submit', this.boundSubmit, true);
        this.form.customColorPicker = this;
      }

      this.openStep('size', { scroll: false });
    }

    disconnectedCallback() {
      if (this.ioObserver) this.ioObserver.disconnect();
      if (this.boxObserver) this.boxObserver.disconnect();
      if (this.form && this.boundSubmit) this.form.removeEventListener('submit', this.boundSubmit, true);
    }

    // ----- steps -------------------------------------------------------------
    initStep(step) {
      const toggle = step.querySelector('[data-mm-step-toggle]');
      if (toggle) toggle.addEventListener('click', () => this.openStep(step.dataset.mmStep));
      const next = step.querySelector('[data-mm-continue]');
      if (next) next.addEventListener('click', () => this.advanceFrom(step.dataset.mmStep));
      const back = step.querySelector('[data-mm-back]');
      if (back) back.addEventListener('click', () => this.openPrevious(step));
    }

    visibleSteps() {
      return this.steps.filter((s) => !s.hidden);
    }

    openStep(name, opts) {
      this.steps.forEach((step) => {
        const open = step.dataset.mmStep === name;
        step.classList.toggle('is-open', open);
        const body = step.querySelector('[data-mm-step-body]');
        if (body) body.hidden = !open;
      });
      if (name === 'front' || name === 'back') {
        if (this.board) this.board.setCurrent(name);
      }
      if (!(opts && opts.scroll === false)) {
        const step = this.steps.find((s) => s.dataset.mmStep === name);
        if (step) step.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }

    openPrevious(step) {
      const list = this.visibleSteps();
      const i = list.indexOf(step);
      if (i > 0) this.openStep(list[i - 1].dataset.mmStep);
    }

    advanceFrom(name) {
      const list = this.visibleSteps();
      const i = list.findIndex((s) => s.dataset.mmStep === name);
      if (i > -1 && i + 1 < list.length) this.openStep(list[i + 1].dataset.mmStep);
      else this.openStep(name, { scroll: false });
    }

    // ----- one side's colour -------------------------------------------------
    initSide(side) {
      const name = side.dataset.mmSide;
      const well = side.querySelector('[data-mm-color]');
      const hex = side.querySelector('[data-mm-hex]');
      const error = side.querySelector('[data-mm-error]');

      const apply = (value, { fromWell } = {}) => {
        const clean = normaliseHex(value);
        if (error) error.hidden = !!clean || !String(value || '').trim();
        if (!clean) {
          if (!String(value || '').trim()) this.setColor(name, null);
          return;
        }
        if (well && !fromWell) well.value = clean;
        if (hex && document.activeElement !== hex) hex.value = clean;
        this.setColor(name, clean);
      };

      if (well) well.addEventListener('input', () => apply(well.value, { fromWell: true }));
      if (hex) {
        hex.addEventListener('input', () => apply(hex.value));
        hex.addEventListener('blur', () => {
          const clean = normaliseHex(hex.value);
          if (clean) hex.value = clean;
        });
      }
      side.querySelectorAll('[data-mm-preset]').forEach((btn) => {
        btn.addEventListener('click', () => {
          apply(btn.dataset.mmPreset);
          side.querySelectorAll('[data-mm-preset]').forEach((b) => b.classList.toggle('is-active', b === btn));
        });
      });
    }

    setColor(name, hex) {
      this.colors[name] = hex;
      const side = this.sides.find((s) => s.dataset.mmSide === name);
      if (side) {
        side.dataset.mmDone = hex ? 'true' : 'false';
        const value = side.querySelector('[data-mm-step-value]');
        if (value) value.textContent = hex || value.dataset.empty || '';
        const chip = side.querySelector('[data-mm-step-chip]');
        if (chip) {
          chip.hidden = !hex;
          chip.style.background = hex || '';
        }
      }
      if (this.board) this.board.setFaceColor(name, hex, hex);
      // Once they have committed to a colour the demo has made its point, and would only
      // compete with the board they are now building.
      if (hex) this.withAmbient((el) => el.stop());
      this.syncProperties();
    }

    // ----- variant picker ----------------------------------------------------
    adoptVariantPicker() {
      const slot = this.querySelector('[data-mm-variant-slot]');
      const picker = this.container && this.container.querySelector('.option-selectors');
      if (!slot || !picker) return;
      if (!slot.contains(picker)) slot.appendChild(picker);

      const update = () => {
        const variant = this.variantFromForm();
        const values = variant ? [variant.option1, variant.option2, variant.option3].filter(Boolean) : [];
        const sizeValue = values.find((v) => SIZE_RE.test(v)) || null;
        const m = sizeValue && sizeValue.match(SIZE_RE);
        const material = values.find((v) => v !== sizeValue && !/sided/i.test(v)) || null;

        // The Sides option is what decides whether a back colour is wanted at all.
        this.doubleSided = !values.some((v) => /single\s*sided/i.test(v));
        const backStep = this.steps.find((s) => s.dataset.mmStep === 'back');
        if (backStep) backStep.hidden = !this.doubleSided;
        if (!this.doubleSided) this.setColor('back', null);
        if (this.board) this.board.setFlippable(this.doubleSided);
        this.syncAmbient();

        const step = this.steps.find((s) => s.dataset.mmStep === 'size');
        const value = step && step.querySelector('[data-mm-step-value]');
        if (value && variant) value.textContent = values.join(' / ');

        if (this.board) {
          if (m) this.board.setSize(parseFloat(m[1]), parseFloat(m[2]));
          this.board.setMaterial(/vinyl/i.test(material || ''), values.join(' / '), material);
        }
        this.syncProperties();
      };

      const jq = themeJQ();
      if (jq) {
        this.variantHandler = () => update();
        jq(window).on('cc-variant-updated', this.variantHandler);
      }
      const idInput = this.form && this.form.querySelector('[name="id"]');
      if (idInput) idInput.addEventListener('change', update);
      update();
      this.applyDefaultSize();
      // one more go once everything has finished loading, in case the retries ran out first
      if (document.readyState !== 'complete') {
        window.addEventListener('load', () => this.applyDefaultSize(), { once: true });
      }

      // Canopy builds its clicky boxes after this component initialises.
      if (!picker.querySelector('.clickyboxes')) {
        this.boxObserver = new MutationObserver(() => {
          if (!picker.querySelector('.clickyboxes')) return;
          this.boxObserver.disconnect();
          this.boxObserver = null;
          update();
        });
        this.boxObserver.observe(picker, { childList: true, subtree: true });
      }
    }

    // Landing cold, the product would otherwise open on whichever variant is first. Only
    // applied when the shopper has not asked for a variant themselves. Canopy binds its own
    // change handler late, so confirm the variant actually moved and retry if it did not.
    applyDefaultSize(attempt) {
      attempt = attempt || 0;
      const wanted = (this.dataset.defaultSize || '').trim();
      if (!wanted || this.defaultSizeApplied) return;
      try {
        if (new URLSearchParams(window.location.search).get('variant')) return;
      } catch (e) { /* no URLSearchParams: just carry on */ }

      const waits = [0, 150, 400, 900, 1600, 2500, 3500];
      const retry = () => {
        if (attempt >= waits.length - 1) return;
        window.setTimeout(() => this.applyDefaultSize(attempt + 1), waits[attempt]);
      };

      const host = (this.container || document).querySelector('.option-selectors');
      const selects = host
        ? Array.from(host.querySelectorAll('select')).filter((el) => el.name !== 'id')
        : [];
      if (!selects.length) return retry();

      const norm = (v) => (v || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const idInput = this.form && this.form.querySelector('[name="id"]');
      const before = idInput ? idInput.value : null;

      let target = null;
      selects.forEach((select) => {
        const match = Array.from(select.options).find((o) => norm(o.value) === norm(wanted));
        if (!match) return;
        select.value = match.value;
        target = select;
      });
      if (!target) { this.defaultSizeApplied = true; return; }

      const jq = themeJQ();
      if (jq) jq(target).trigger('change');
      else target.dispatchEvent(new Event('change', { bubbles: true }));

      if (idInput && idInput.value === before) return retry();
      this.defaultSizeApplied = true;
    }

    // Both this and custom-color-ambient.js load deferred, and nothing orders them, so on a
    // cold load the first variant update can run before <mix-match-ambient> is upgraded and
    // has any methods at all. Guarding on the method and moving on silently left the demo
    // stuck on its single sided default - landing on a Double Sided variant never flipped.
    withAmbient(fn) {
      if (!this.ambient) return;
      if (typeof this.ambient.stop === 'function') return fn(this.ambient);
      customElements.whenDefined('mix-match-ambient').then(() => {
        if (this.ambient) fn(this.ambient);
      });
    }

    syncAmbient() {
      this.withAmbient((el) => el.setDoubleSided(this.doubleSided));
    }

    variantFromForm() {
      const select = this.form && this.form.querySelector('[name="id"]');
      const productId = this.form && this.form.dataset.productId;
      const jsonEl = productId && document.getElementById('ccProductJson-' + productId);
      if (!select || !jsonEl) return null;
      try {
        const product = JSON.parse(jsonEl.textContent);
        return product.variants.find((v) => String(v.id) === String(select.value)) || null;
      } catch (e) {
        return null;
      }
    }

    // ----- submission --------------------------------------------------------
    syncProperties() {
      this.querySelectorAll('[data-mm-property]').forEach((input) => {
        const side = input.dataset.mmProperty;
        const wanted = side === 'back' && !this.doubleSided ? null : this.colors[side];
        input.value = wanted || '';
        input.disabled = !wanted;
      });
    }

    setError(side, message) {
      const el = side && side.querySelector('[data-mm-error]');
      if (!el) return;
      el.textContent = message;
      el.hidden = !message;
    }

    onSubmit(event) {
      const missing = [];
      if (!this.colors.front) missing.push('front');
      if (this.doubleSided && !this.colors.back) missing.push('back');
      if (!missing.length) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      const first = missing[0];
      const side = this.sides.find((s) => s.dataset.mmSide === first);
      this.setError(side, (window.theme && theme.strings && theme.strings.customColorMissing) ||
        'Please choose a colour for the ' + first + ' before adding to cart.');
      this.openStep(first);
    }

    // This picker replaces the Infinite Options fields for this product; if the app still
    // injects its required inputs they would block add-to-cart.
    retireInfiniteOptions() {
      const scope = this.container || document;
      const purge = () => scope.querySelectorAll('#infiniteoptions-container, .infiniteoptions-container')
        .forEach((el) => el.remove());
      purge();
      this.ioObserver = new MutationObserver(purge);
      this.ioObserver.observe(scope, { childList: true, subtree: true });
    }
  }

  if (!customElements.get('custom-color-picker')) {
    customElements.define('custom-color-picker', CustomColorPicker);
  }
})();
